<script setup lang="ts">
import DropFile from '@legacy/components/common/form/input/DropFile.vue';
import WysiwygEditor from '@legacy/components/WysiwygEditor.vue';
import ApiError from '@legacy/js/model/Error';
import axios from 'axios';
import { computed, ref } from 'vue';
import { SphButton } from '@/components/base/button';
import { SphCheckbox } from '@/components/base/checkbox';
import { SphDatePicker } from '@/components/base/date-picker';
import { SphField } from '@/components/base/field';
import { SphInput } from '@/components/base/input';
import { SphRadioGroup } from '@/components/base/radio-group';
import { SphSelect } from '@/components/base/select';
import { useI18n } from '@/composables/i18n';
import { useNotifications } from '@/composables/notifications';
import { useDialog } from '@/plugins/dialog';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{
    federationId: number
    target: FederationNewsTarget
    institutions: FederationNewsInstitution[]
    preselectedIds?: number[]
    news?: FederationNewsItem | null
}>();

const emit = defineEmits<{
    cancel: []
    saved: [id: string]
}>();

export interface FederationNewsInstitution {
    id: number
    name: string
}

export type FederationNewsTarget = 'clubs' | 'kindergartens';
export type FederationNewsAudience = 'staff' | 'everyone' | 'public';

export interface FederationNewsItem {
    id: string
    title: string
    teaser: string | null
    text: string | null
    image: string | null
    publishDate: string | null
    publishedEnd: string | null
    topNews: boolean
    audience: FederationNewsAudience
    target: FederationNewsTarget
    clubs: FederationNewsInstitution[]
    kindergartens: FederationNewsInstitution[]
}

const { i18n } = useI18n();
const { notify } = useNotifications();
const { dialog } = useDialog();

const isEdit = computed(() => !!props.news);
const editorId = `federation-news-editor-${Math.random().toString(36).slice(2)}`;
const textareaId = `${editorId}-source`;

function initialTargetIds(): string[] {
    if (props.news) {
        return (props.target === 'clubs' ? props.news.clubs : props.news.kindergartens).map((i) => String(i.id));
    }

    return (props.preselectedIds ?? []).map(String);
}

const title = ref(props.news?.title ?? '');
const teaser = ref(props.news?.teaser ?? '');
const initialText = props.news?.text ?? '';
const textarea = ref<HTMLTextAreaElement | null>(null);
const publishLater = ref(!!props.news?.publishDate && new Date(props.news.publishDate) > new Date());
const publishDate = ref<string | null>(props.news?.publishDate ?? null);
const publishedEnd = ref<string | null>(props.news?.publishedEnd ?? null);
const topNews = ref(props.news?.topNews ?? false);
const audience = ref<FederationNewsAudience>(props.news?.audience ?? 'staff');
const targetIds = ref<string[]>(initialTargetIds());
const imageFile = ref<File | null>(null);
const removeImage = ref(false);
const saving = ref(false);
const apiError = ref<InstanceType<typeof ApiError> | null>(null);

const institutionOptions = computed(() => props.institutions.map((i) => ({ value: String(i.id), label: i.name })));
const audienceOptions = computed(() => {
    if (props.target === 'clubs') {
        return [
            { value: 'staff', label: i18n.t('Diese Nachricht ist intern und nur für Mitarbeiter des Vereins sichtbar') },
            { value: 'everyone', label: i18n.t('Für alle Mitarbeiter, alle Mitglieder und Gäste freigeben') },
        ];
    }

    return [
        { value: 'staff', label: i18n.t('Diese Nachricht ist intern und nur für Mitarbeiter der Einrichtung sichtbar') },
        { value: 'everyone', label: i18n.t('Für alle Mitarbeiter und Eltern freigeben') },
        { value: 'public', label: i18n.t('Für alle Mitarbeiter und Eltern freigeben und auf der Website der Einrichtung veröffentlichen') },
    ];
});
const labels = computed(() => {
    if (props.target === 'clubs') {
        return {
            selectAll: i18n.t('Alle Vereine auswählen'),
            institutions: i18n.t('Vereine'),
            placeholder: i18n.t('Vereine auswählen'),
            removalHint: i18n.t('Vereine, die aus der Auswahl entfernt werden, verlieren ihre Kopie der Nachricht.'),
        };
    }

    return {
        selectAll: i18n.t('Alle Einrichtungen auswählen'),
        institutions: i18n.t('Einrichtungen'),
        placeholder: i18n.t('Einrichtungen auswählen'),
        removalHint: i18n.t('Einrichtungen, die aus der Auswahl entfernt werden, verlieren ihre Kopie der Nachricht.'),
    };
});
const currentImageUrl = computed(() => {
    if (!props.news?.image || removeImage.value) {
        return null;
    }
    return `/backend/thumbnail/federationNews/${props.news.id}/news_list_thumb/${props.news.image}`;
});
const allSelected = computed(() => props.institutions.length > 0 && targetIds.value.length === props.institutions.length);
const canSubmit = computed(() => title.value.trim() !== '' && targetIds.value.length > 0 && !saving.value);

function toggleAll(selectAll: boolean) {
    targetIds.value = selectAll ? props.institutions.map((i) => String(i.id)) : [];
}

function handleFiles(files: File[]) {
    imageFile.value = files[0] ?? null;
    if (imageFile.value) {
        removeImage.value = true;
    }
}

function buildRequestData(notify: boolean) {
    return {
        title: title.value,
        teaser: teaser.value === '' ? null : teaser.value,
        text: textarea.value?.value ?? initialText,
        publishDate: publishLater.value ? publishDate.value : null,
        publishedEnd: publishedEnd.value,
        topNews: topNews.value,
        audience: audience.value,
        notify,
        target: props.target,
        targetIds: targetIds.value.map((id) => Number.parseInt(id)),
    };
}

async function askToNotify(): Promise<boolean | null> {
    const willPublishLater = publishLater.value && !!publishDate.value && new Date(publishDate.value) > new Date();

    return dialog.confirm({
        title: i18n.t('Benachrichtigungen versenden?'),
        message: willPublishLater
            ? i18n.t('Sollen alle Personen, für die dieser Eintrag freigegeben ist, zum Zeitpunkt der Veröffentlichung automatisch darüber benachrichtigt werden?')
            : i18n.t('Sollen alle Personen, für die dieser Eintrag freigegeben ist, jetzt automatisch über die Erstellung / Änderung benachrichtigt werden?'),
        confirmButtonLabel: i18n.t('Ja'),
        cancelButtonLabel: i18n.t('Nein'),
    });
}

async function save() {
    apiError.value = null;
    const notifyRecipients = await askToNotify();
    if (notifyRecipients === null) {
        return;
    }
    const data = buildRequestData(notifyRecipients);
    const base = `/federations/${props.federationId}/news`;

    saving.value = true;
    try {
        let id = props.news?.id;
        if (isEdit.value) {
            await axios.put(`${base}/${id}`, data);
        } else {
            const response = await axios.post(base, data);
            id = response.headers['x-identifier'];
        }

        if (imageFile.value) {
            await axios.put(`${base}/${id}/image`, imageFile.value, { headers: { 'Content-Type': imageFile.value.type } });
        } else if (removeImage.value && props.news?.image) {
            await axios.delete(`${base}/${id}/image`);
        }

        emit('saved', id as string);
    } catch (err: any) {
        apiError.value = ApiError.fromResponse(err.response);
        notify.error(err.response?.data?.error?.message ?? i18n.t('Ein Fehler ist aufgetreten.'));
    } finally {
        saving.value = false;
    }
}
</script>

<template>
    <div>
        <div class="kgr-grid kgr-grid--full kgr-grid--medium-fit kgr-grid--gutters kgr-grid--bigGutters">
            <div class="kgr-grid__cell space-y-4">
                <SphField
                    v-slot="field"
                    :label="i18n.t('Titel')"
                    required
                    :errors="apiError?.messagesForField('title', true)"
                >
                    <SphInput
                        v-bind="field"
                        v-model="title"
                        :maxlength="255"
                    />
                </SphField>
                <SphField
                    v-slot="field"
                    :label="i18n.t('Teaser')"
                    :errors="apiError?.messagesForField('teaser', true)"
                >
                    <SphInput
                        v-bind="field"
                        v-model="teaser"
                        :maxlength="100"
                    />
                </SphField>
                <SphField
                    :label="i18n.t('Text')"
                    :errors="apiError?.messagesForField('text', true)"
                >
                    <textarea
                        :id="textareaId"
                        ref="textarea"
                        style="display: none"
                        :value="initialText"
                    ></textarea>
                    <WysiwygEditor
                        :editor-id="editorId"
                        :content-selector="`#${textareaId}`"
                        height="300px"
                        mini
                        resizable
                    />
                </SphField>
                <SphField :label="i18n.t('Bild')">
                    <div
                        v-if="currentImageUrl"
                        class="mb-2 flex items-center gap-4"
                    >
                        <img
                            :src="currentImageUrl"
                            alt=""
                            style="max-height: 120px"
                        />
                        <SphButton
                            :label="i18n.t('Bild entfernen')"
                            size="sm"
                            @click="removeImage = true"
                        />
                    </div>
                    <DropFile
                        jpg-png-only
                        emit-files
                        show-size
                        :max-file-size="15"
                        @files="handleFiles"
                    />
                </SphField>
            </div>
            <div class="kgr-grid__cell space-y-4">
                <SphField
                    v-slot="field"
                    :label="i18n.t('Später veröffentlichen')"
                    inline
                    compact
                >
                    <SphCheckbox
                        v-bind="field"
                        v-model="publishLater"
                    />
                </SphField>
                <SphField
                    v-if="publishLater"
                    :label="i18n.t('Veröffentlichen am')"
                    :errors="apiError?.messagesForField('publishDate', true)"
                >
                    <SphDatePicker
                        v-model="publishDate"
                        model-type="iso-atom"
                        time-input
                    />
                </SphField>
                <SphField
                    :label="i18n.t('Veröffentlichung endet am')"
                    :errors="apiError?.messagesForField('publishedEnd', true)"
                >
                    <SphDatePicker
                        v-model="publishedEnd"
                        model-type="date-string"
                        clearable
                    />
                </SphField>
                <SphField
                    v-slot="field"
                    :label="i18n.t('Als Top-News markieren')"
                    inline
                    compact
                >
                    <SphCheckbox
                        v-bind="field"
                        v-model="topNews"
                    />
                </SphField>
                <SphField
                    :label="i18n.t('Freigabe')"
                    :errors="apiError?.messagesForField('audience', true)"
                >
                    <SphRadioGroup
                        v-model="audience"
                        :options="audienceOptions"
                    />
                </SphField>
                <SphField
                    v-slot="field"
                    :label="labels.selectAll"
                    inline
                    compact
                >
                    <SphCheckbox
                        v-bind="field"
                        :model-value="allSelected"
                        @update:model-value="toggleAll($event === true)"
                    />
                </SphField>
                <SphField
                    :label="labels.institutions"
                    required
                    :errors="apiError?.messagesForField('targetIds', true)"
                >
                    <SphSelect
                        v-model="targetIds"
                        :options="institutionOptions"
                        :placeholder="labels.placeholder"
                        multiple
                    />
                </SphField>
                <div
                    v-if="isEdit"
                    class="kgr-alert kgr-alert--info"
                >
                    {{ labels.removalHint }}
                </div>
            </div>
        </div>
        <br />
        <div class="kgr-postfix kgr-postfix--between">
            <div class="kgr-postfix__fixed">
                <SphButton @click="emit('cancel')">
                    {{ i18n.t('Abbrechen') }}
                </SphButton>
            </div>
            <div class="kgr-postfix__fixed">
                <SphButton
                    variant="primary"
                    :disabled="!canSubmit"
                    :loading="saving"
                    @click="save"
                >
                    {{ isEdit ? i18n.t('Speichern') : i18n.t('Senden') }}
                </SphButton>
            </div>
        </div>
    </div>
</template>
